/**
 * Blow Room Production Form — Layer 2.
 *
 * Operator types only:
 *   • actual_output_kg
 *   • waste_kg
 *   • contamination yes/no
 *   • abnormal stoppage reason (optional)
 *
 * Everything else is auto-fetched / auto-calculated by the Layer 1
 * spine (waste %, efficiency from breakdowns, plan achievement %,
 * cost flow). The page submits to the existing /production-logs
 * endpoint with department_code = 'BLOW_ROOM' and machine_id pinned
 * to the selected blowroom line.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, Form, Input, InputNumber, Row,
  Select, Space, Switch, Table, Tag, Typography,
} from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import { productionLogsApi } from '@/features/reports/api'
import { ReadOnlyTile } from './_shared'
import { useOperationsContext } from './_context'

const { Text } = Typography

export default function BlowRoomProductionPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [plantId, setPlantId] = useState<string | undefined>()
  const [machineId, setMachineId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()

  // Sprint 0074 review — auto-context: plant, current shift, BR lines,
  // active laydown all come pre-resolved. Operator just confirms +
  // types output. The form still allows override on every field.
  const ctx = useOperationsContext({ preferredPlantId: plantId })
  const { plantOptions, defaultPlantId, shifts, currentShift,
          blowroomMachines: brMachines, activeLaydown } = ctx

  // Auto-pick plant / shift / line on first context load.
  useEffect(() => { if (!plantId && defaultPlantId) setPlantId(defaultPlantId) }, [defaultPlantId])
  useEffect(() => { if (!shiftId && currentShift?.id) setShiftId(currentShift.id) }, [currentShift?.id])
  useEffect(() => {
    // Single BR line → auto-pick. Multi-line → operator picks.
    if (!machineId && brMachines.length === 1) setMachineId(brMachines[0].id)
  }, [brMachines.length])

  // Recent BR production logs for sanity / duplicate-warning.
  const today = dayjs().format('YYYY-MM-DD')
  const { data: recentLogs = [] } = useQuery({
    queryKey: ['br-prod-recent', plantId, today],
    queryFn: () => productionLogsApi.listLogs({
      plant_id: plantId!, department_code: 'BLOW_ROOM',
    }),
    enabled: !!plantId,
  })

  // Duplicate-entry warning.
  const dupRow = useMemo(() => {
    if (!plantId || !machineId || !shiftId) return null
    return (recentLogs || []).find((l: any) =>
      l.machine_id === machineId
      && l.shift_id === shiftId
      && l.log_date === today
    )
  }, [recentLogs, plantId, machineId, shiftId, today])

  const saveMutation = useMutation({
    mutationFn: (payload: any) => productionLogsApi.createLog(payload),
    onSuccess: () => {
      message.success('Blow Room production log saved')
      form.resetFields(['actual_output_kg', 'total_waste_kg', 'remarks'])
      qc.invalidateQueries({ queryKey: ['br-prod-recent'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Save failed'),
  })

  useEffect(() => {
    form.setFieldsValue({
      log_date: dayjs().format('YYYY-MM-DD'),
    })
  }, [form])

  const onSubmit = async () => {
    const v = await form.validateFields()
    if (!plantId || !machineId || !shiftId) {
      message.error('Plant, BR line and shift are required')
      return
    }
    saveMutation.mutate({
      plant_id: plantId,
      machine_id: machineId,
      shift_id: shiftId,
      log_date: today,
      department_code: 'BLOW_ROOM',
      actual_output_kg: v.actual_output_kg,
      total_waste_kg: v.total_waste_kg ?? null,
      total_stoppage_minutes: v.total_stoppage_minutes ?? null,
      remarks: [
        v.contamination_found ? 'Contamination found.' : null,
        v.stoppage_reason || null,
        v.remarks || null,
      ].filter(Boolean).join(' '),
    })
  }

  const cols = [
    { title: 'Date', dataIndex: 'log_date', width: 100 },
    { title: 'Shift', dataIndex: 'shift_name', width: 90,
      render: (_: any, r: any) => r.shift_name || r.shift_id?.slice(0, 8) },
    { title: 'Output (kg)', dataIndex: 'actual_output_kg', width: 110,
      align: 'right' as const, render: (v: any) => v ? Number(v).toFixed(2) : '—' },
    { title: 'Waste %', dataIndex: 'waste_pct', width: 90, align: 'right' as const,
      render: (v: any) => v != null ? <Tag color="orange">{Number(v).toFixed(2)}%</Tag> : '—' },
    { title: 'Eff %', dataIndex: 'machine_efficiency_pct', width: 90, align: 'right' as const,
      render: (v: any) => v != null ? <Tag color="blue">{Number(v).toFixed(1)}%</Tag> : '—' },
    { title: 'Plan Ach %', dataIndex: 'plan_achievement_pct', width: 110, align: 'right' as const,
      render: (v: any) => v != null ? Number(v).toFixed(1) + '%' : '—' },
    { title: 'Remarks', dataIndex: 'remarks', ellipsis: true },
  ]

  return (
    <div>
      <PageHeader
        title="Blow Room — Production"
        subtitle="Operator enters output + waste. Waste %, efficiency, achievement, cost — all auto."
      />
      <Row gutter={16}>
        <Col xs={24} lg={14}>
          <Card>
            <Form layout="vertical" form={form} size="large">
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item label="Plant" required>
                    <Select
                      options={plantOptions} value={plantId}
                      onChange={setPlantId} placeholder="Select plant"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Blow Room Line" required>
                    <Select
                      options={brMachines.map((m: any) => ({
                        label: `${m.machine_id_str} — ${m.name}`,
                        value: m.id,
                      }))}
                      value={machineId} onChange={setMachineId}
                      placeholder={brMachines.length ? 'Select BR line' : 'Add BR machine in Masters'}
                      disabled={!plantId}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item label="Shift" required>
                    <Select
                      options={(shifts || []).map((s: any) => ({
                        label: s.shift_name || s.shift_code, value: s.id,
                      }))}
                      value={shiftId} onChange={setShiftId} placeholder="Select shift"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Date" name="log_date">
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>

              {dupRow && (
                <Alert
                  type="warning" showIcon style={{ marginBottom: 12 }}
                  message="A log already exists for this BR line + shift today"
                  description="Save will create a separate row. Edit the existing one if you meant to update it."
                />
              )}

              <Row gutter={12}>
                <Col xs={12}>
                  <Form.Item
                    name="actual_output_kg" label="Actual output (kg)"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <InputNumber style={{ width: '100%' }} step={1} min={0} inputMode="decimal" />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item name="total_waste_kg" label="Waste kg (optional, manual / scale)">
                    <InputNumber style={{ width: '100%' }} step={1} min={0} inputMode="decimal" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={12} md={8}>
                  <Form.Item name="contamination_found" label="Contamination found?" valuePropName="checked">
                    <Switch checkedChildren="YES" unCheckedChildren="NO" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item name="total_stoppage_minutes" label="Stop minutes (if any)">
                    <InputNumber style={{ width: '100%' }} min={0} step={5} inputMode="numeric" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="stoppage_reason" label="Stoppage reason (only if abnormal)">
                    <Input placeholder="e.g. duct choking" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="remarks" label="Remarks (only if exception)">
                <Input.TextArea rows={2} maxLength={300} />
              </Form.Item>

              <Button
                type="primary" size="large" block icon={<SaveOutlined />}
                loading={saveMutation.isPending} onClick={onSubmit}
              >
                Save
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Auto-fetched / auto-calculated">
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <ReadOnlyTile label="Date" value={dayjs().format('DD-MMM-YYYY')} hint="From system" />
              </Col>
              <Col span={12}>
                <ReadOnlyTile
                  label="Current shift"
                  value={currentShift
                    ? `${currentShift.shift_name} (${currentShift.start_time?.slice(0,5)}–${currentShift.end_time?.slice(0,5)})`
                    : '—'}
                  hint={currentShift ? 'Picked from now' : 'No shift covers now'}
                  source={currentShift ? 'AUTO' : 'MANUAL'}
                />
              </Col>
              <Col span={24}>
                <ReadOnlyTile
                  label="Active Laydown (linkage upstream)"
                  value={activeLaydown ? activeLaydown.laydown_no : 'None'}
                  hint={activeLaydown
                    ? `${activeLaydown.count_description || ''} · status ${activeLaydown.status}`
                    : 'Start a laydown in Bale Management → Laydown Planning'}
                  source={activeLaydown ? 'SYSTEM' : 'MANUAL'}
                />
              </Col>
              <Col span={12}>
                <ReadOnlyTile label="Operator" value="logged-in user"
                  hint="Stamped on save" source="SYSTEM" />
              </Col>
              <Col span={12}>
                <ReadOnlyTile label="BR Lines available"
                  value={`${brMachines.length} line(s)`}
                  hint={brMachines.length === 1 ? 'Auto-picked' : 'Multiple — select one'}
                  source={brMachines.length === 1 ? 'AUTO' : 'MANUAL'}
                />
              </Col>
              <Col span={24}>
                <ReadOnlyTile
                  label="Auto-derived after save"
                  value="waste % • efficiency • plan achievement • cost"
                  hint="From actual_output, waste, breakdowns + linked laydown"
                  source="SYSTEM"
                />
              </Col>
            </Row>
          </Card>

          <Card title="Today's BR logs (for reference)" style={{ marginTop: 12 }}>
            <Table
              size="small" rowKey="id" pagination={false}
              columns={cols} dataSource={recentLogs as any[]}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </Col>
      </Row>
      <div style={{ marginTop: 12, fontSize: 11, color: '#888' }}>
        <Text type="secondary">
          Tip — efficiency is auto-derived from MachineBreakdown rows for this BR line + shift + date.
          Use the Maintenance form to log breakdowns; this page does not need them re-entered.
        </Text>
      </div>
    </div>
  )
}
